// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { TextFieldProps } from "@insite/mobius/TextField";


import QtyField, { QtyFieldStyles } from "@pace/App/Components/Qty/QtyField";

import { mergeWithCss } from "@pace/XDL/Utilities";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: ProductQuantityStyles; // XNG-Change type to be QTY Field
    showLabel?: boolean;
    printLabelOverride?: React.ReactNode;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

// XNG-Changes: Styles & Structure
// export interface ProductQuantityOrderedActionsStyles {
//     wrapper?: InjectableCss;
//     actionClickable?: ClickableProps;
//     quantityOrderedTextField?: TextFieldPresentationProps;
//     withQuantityTooltipStyles?: InjectableCss;
// }
// export interface ProductQuantityOrderedStyles extends TextFieldPresentationProps {
//     actionsStyles?: ProductQuantityOrderedActionsStyles;
//     print?: InjectableCss;
//     multipleSaleQtyWrapper?: InjectableCss;
//     questionIconStyles?: IconPresentationProps;
//     multipleSaleQtyToolTip?: TooltipPresentationProps;
// }
export interface ProductQuantityStyles {
    qtyFieldStyles?: QtyFieldStyles;
}

// export const productQuantityOrderedStyles: ProductQuantityOrderedStyles = {
//     sizeVariant: "small",
//     cssOverrides: {
//         formInputWrapper: css`
//             input[type="number"]::-webkit-outer-spin-button,
//             input[type="number"]::-webkit-inner-spin-button {
//                 /* ISC-11023 deal with this. */
//                 /* stylelint-disable-next-line */
//                 -webkit-appearance: none;
//                 margin: 0;
//             }
//         `,
//         inputSelect: css`
//             /* ISC-11023 deal with this. */
//             /* stylelint-disable-next-line */
//             -moz-appearance: textfield;
//             width: auto;
//             text-align: center;
//             @media print {
//                 display: none;
//             }
//         `,
//     },
//     print: {
//         css: css`
//             display: none;
//             @media print {
//                 display: block;
//             }
//         `,
//     },
// };

export const productQuantityStyles: ProductQuantityStyles = {};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        // XNG-Changes: add allowZeroPricing, detail
        product: { id: productId, allowZeroPricing, minimumOrderQty, detail },
        onQtyOrderedChanged,
        // XNG-Changes: add pricing
        productInfo: { qtyOrdered, failedToLoadInventory, pricing },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    showLabel,
    ...otherProps
}: Props) => {
    // Change styles
    const [styles] = useState(() => mergeWithCss(productQuantityStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());
    const [searchTimeoutId, setSearchTimeoutId] = useState<number | undefined>(undefined);

    useEffect(() => {
        // Xng-Change: allow removing qty number setting to ''
        if(!qtyOrdered) {
            setProductQty("");
            return;
        }
        if (qtyOrdered === Number(productQty) && (!minimumOrderQty || qtyOrdered >= minimumOrderQty)) {
            return;
        }
        if (minimumOrderQty && qtyOrdered < minimumOrderQty) {
            setProductQty(`${minimumOrderQty}`);
            onQtyOrderedChanged && onQtyOrderedChanged(minimumOrderQty);
            return;
        }

        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    /**
     * Xng-Change
     * on Blur if qtyOrdered is 0 or '' return it to 1 or minimumOrderQty
     * @param event 
     */
    const handleBlur = (event?: React.ChangeEvent<HTMLInputElement>) => {
        if(!qtyOrdered) {
            let value = "1";
            const quantityValue = Number(value);

            setProductQty(value);

            if (typeof searchTimeoutId === "number") {
                clearTimeout(searchTimeoutId);
            }

            setSearchTimeoutId(
                setTimeout(() => {
                    if (minimumOrderQty && minimumOrderQty !== 0 && quantityValue % minimumOrderQty !== 0) {
                        value = (quantityValue - (quantityValue % minimumOrderQty) + minimumOrderQty).toString();
                    }
                    setProductQty(value);

                    if (!onQtyOrderedChanged) {
                        logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
                        return;
                    }

                    const possibleNumber = Number(value);
                    if (!Number.isNaN(possibleNumber)) {
                        onQtyOrderedChanged(possibleNumber);
                    }
                }, 250),
            );
            
        }
    };

    const disabled =
        (!hasEnoughInventory && !failedToLoadInventory) ||
        !canAddToCart ||
        (!allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0);

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;
        const quantityValue = Number(value);
        setProductQty(value);

        if (typeof searchTimeoutId === "number") {
            clearTimeout(searchTimeoutId);
        }

        setSearchTimeoutId(
            setTimeout(() => {
                if (minimumOrderQty && minimumOrderQty !== 0 && quantityValue % minimumOrderQty !== 0) {
                    value = (quantityValue - (quantityValue % minimumOrderQty) + minimumOrderQty).toString();
                }
                setProductQty(value);

                if (!onQtyOrderedChanged) {
                    logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
                    return;
                }

                const possibleNumber = Number(value);
                if (!Number.isNaN(possibleNumber)) {
                    onQtyOrderedChanged(possibleNumber);
                }
            }, 250),
        );
    };

    // XNG-CHANGES: replace textfield by QtyField component
    return (
        <QtyField
            label={labelOverride ?? translate("QTY")}
            value={productQty}
            type="qty"
            min={minimumOrderQty || detail?.multipleSaleQty || 1}
            max={9999}
            multipleSaleQty={detail?.multipleSaleQty}
            {...otherProps}
            onKeyPress={keyPressHandler}
            onChange={qtyChangeHandler}
            onBlur={handleBlur}
            showLabel={showLabel}
            extendedStyles={styles.qtyFieldStyles}
            disabled={disabled}
            data-test-selector="product_qtyOrdered"
        />
    );

};

ProductQuantityOrdered.defaultProps = {
    showLabel: false,
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
